import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TextField, Input, SearchField, Button, Skeleton, ScrollShadow } from '@heroui/react';
import { SelectMenu } from '@/components/SelectMenu';
import { toast } from '@/lib/toast';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';
import { buildPublicInviteUrl } from '@/lib/inviteLinks';
import { useFriends } from '@/lib/useFriends';
import { useDataStore } from '@/stores/dataStore';
import { avatarHue } from '@/lib/avatarHue';
import { CloseIcon, CopyIcon, SettingsIcon } from '@/components/icons';
import type { FriendEntry, Invite } from '@/types/api';

/**
 * Discord-style "Пригласить друзей на сервер" modal. Opens from the server
 * right-click menu (HeroTopBar → ServerContextMenu). Three parts:
 *
 *   - per-friend invite row with a one-click "Пригласить" that opens a DM
 *     with that friend and drops the invite link in as a message
 *   - a copyable invite link
 *   - a collapsible "settings" block (expiry / max-uses) that regenerates
 *     the link with new constraints
 *
 * The invite link is generated lazily on open (default 7d / unlimited).
 * Generation requires MANAGE perms server-side; if it 403s we keep the
 * friend-invite flow disabled and surface the reason inline rather than
 * failing silently.
 */
const EXPIRY_OPTIONS: { value: number; labelKey: string }[] = [
  { value: 1800, labelKey: 'gmod.expiry30m' },
  { value: 3600, labelKey: 'gmod.expiry1h' },
  { value: 21600, labelKey: 'gmod.expiry6h' },
  { value: 86400, labelKey: 'gmod.expiry24h' },
  { value: 604800, labelKey: 'gmod.expiry7d' },
  { value: 0, labelKey: 'gmod.expiryNever' },
];
const USES_OPTIONS: { value: number; labelKey: string }[] = [
  { value: 0, labelKey: 'gmod.usesUnlimited' },
  { value: 1, labelKey: 'gmod.uses1' },
  { value: 5, labelKey: 'gmod.uses5' },
  { value: 10, labelKey: 'gmod.uses10' },
  { value: 25, labelKey: 'gmod.uses25' },
  { value: 50, labelKey: 'gmod.uses50' },
];

export function InviteToServerModal(props: { guildId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const guild = useDataStore((s) => s.guilds[props.guildId]);
  const upsertDmChannel = useDataStore((s) => s.upsertDmChannel);
  const { friends, isLoading: friendsLoading } = useFriends();

  const [invite, setInvite] = useState<Invite | null>(null);
  const [genLoading, setGenLoading] = useState(true);
  const [genError, setGenError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [expiresIn, setExpiresIn] = useState(604800);
  const [maxUses, setMaxUses] = useState(0);
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  // Per-friend send state: 'sending' | 'sent' keyed by userId.
  const [sentState, setSentState] = useState<Record<string, 'sending' | 'sent'>>({});

  const inviteUrl = invite ? buildPublicInviteUrl(invite.code) : '';

  // Generate a fresh invite. Called on open (with defaults) and whenever the
  // user applies new settings.
  function generate(expiresInS: number, uses: number) {
    setGenLoading(true);
    setGenError(null);
    api
      .createInvite(props.guildId, {
        expiresInS: expiresInS > 0 ? expiresInS : undefined,
        maxUses: uses > 0 ? uses : undefined,
      })
      .then((inv) => setInvite(inv))
      .catch((err) => {
        const [title] = errToToast(err, t('gmod.createInviteFailed'));
        setGenError(title);
      })
      .finally(() => setGenLoading(false));
  }

  useEffect(() => {
    generate(604800, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.guildId]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return friends;
    return friends.filter((f) => {
      const label = (f.user.displayName || f.user.username).toLowerCase();
      return label.includes(q) || f.user.username.toLowerCase().includes(q);
    });
  }, [friends, q]);

  async function copyLink() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t('gmod.copyFailed'));
    }
  }

  async function inviteFriend(f: FriendEntry) {
    if (!invite || sentState[f.user.id]) return;
    setSentState((s) => ({ ...s, [f.user.id]: 'sending' }));
    try {
      const dm = await api.openDirectMessage(f.user.id);
      upsertDmChannel(dm);
      await api.sendMessage(dm.id, { content: inviteUrl });
      setSentState((s) => ({ ...s, [f.user.id]: 'sent' }));
    } catch (err) {
      setSentState((s) => {
        const next = { ...s };
        delete next[f.user.id];
        return next;
      });
      toast.error(...errToToast(err, t('gmod.sendInviteFailed')));
    }
  }

  function applySettings() {
    generate(expiresIn, maxUses);
    setShowSettings(false);
  }

  return (
    <Modal.Backdrop
      isOpen
      variant="blur"
      onOpenChange={(o) => {
        if (!o) props.onClose();
      }}
    >
      <Modal.Container placement="center">
        <Modal.Dialog
          aria-label={t('gmod.inviteToServer')}
          className="flex w-full max-w-md flex-col overflow-hidden p-0"
          style={{ maxHeight: 'calc(100vh - 2rem)' }}
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-separator px-5 py-4">
            <div className="min-w-0">
              <h2 className="truncate text-base font-semibold text-foreground">
                {t('gmod.inviteFriends')}
              </h2>
              {guild && (
                <p className="truncate text-xs text-muted">{t('gmod.toServerNamed', { name: guild.name })}</p>
              )}
            </div>
            <button
              type="button"
              onClick={props.onClose}
              aria-label={t('gmod.close')}
              className="grid size-7 shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-default/60 hover:text-foreground"
            >
              <CloseIcon className="size-4" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 py-4">
            {friends.length > 5 && (
              <SearchField aria-label={t('gmod.findFriend')} value={search} onChange={setSearch}>
                <SearchField.Group>
                  <SearchField.SearchIcon />
                  <SearchField.Input placeholder={t('gmod.findFriend')} />
                  {search && <SearchField.ClearButton aria-label={t('gmod.clear')} />}
                </SearchField.Group>
              </SearchField>
            )}

            <ScrollShadow hideScrollBar className="-mx-1 min-h-0 flex-1 px-1">
              {friendsLoading ? (
                <ul className="flex flex-col gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <li key={i} className="flex items-center gap-3 rounded-lg px-2 py-1.5">
                      <Skeleton className="size-8 shrink-0 rounded-full" />
                      <Skeleton className="h-3 w-1/3 rounded" />
                    </li>
                  ))}
                </ul>
              ) : filtered.length === 0 ? (
                <div className="grid place-items-center py-8 text-center text-xs text-muted">
                  {q ? t('gmod.noMatch', { query: q }) : t('gmod.noFriendsYet')}
                </div>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {filtered.map((f) => {
                    const label = f.user.displayName || f.user.username;
                    const hue = avatarHue(f.user.id);
                    const state = sentState[f.user.id];
                    return (
                      <li
                        key={f.user.id}
                        className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-default/50"
                      >
                        <span
                          className={
                            'grid size-8 shrink-0 place-items-center overflow-hidden rounded-full text-xs font-medium ' +
                            (f.user.avatarUrl ? '' : `${hue.bg} ${hue.text}`)
                          }
                        >
                          {f.user.avatarUrl ? (
                            <img src={f.user.avatarUrl} alt="" className="size-full object-cover" />
                          ) : (
                            label.charAt(0).toUpperCase()
                          )}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm text-foreground">{label}</span>
                        <Button
                          size="sm"
                          variant={state === 'sent' ? 'ghost' : 'primary'}
                          isDisabled={!invite || !!state}
                          onPress={() => void inviteFriend(f)}
                        >
                          {state === 'sent'
                            ? t('gmod.sent')
                            : state === 'sending'
                              ? t('gmod.sending')
                              : t('gmod.invite')}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </ScrollShadow>

            <div className="shrink-0 border-t border-separator pt-3">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-medium text-muted">
                  {t('gmod.orShareLink')}
                </span>
                <button
                  type="button"
                  onClick={() => setShowSettings((v) => !v)}
                  aria-label={t('gmod.linkSettings')}
                  className={
                    'grid size-6 place-items-center rounded-lg transition-colors hover:bg-default/60 ' +
                    (showSettings ? 'text-accent' : 'text-muted hover:text-foreground')
                  }
                >
                  <SettingsIcon className="size-4" />
                </button>
              </div>

              {genError ? (
                <p className="rounded-md border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                  {genError}
                </p>
              ) : (
                <div className="flex items-center gap-2">
                  <TextField
                    fullWidth
                    aria-label={t('gmod.inviteLink')}
                    value={genLoading ? t('gmod.creatingLink') : inviteUrl}
                    isReadOnly
                  >
                    <Input variant="secondary" className="font-mono text-xs" />
                  </TextField>
                  <Button
                    size="sm"
                    variant="primary"
                    isDisabled={genLoading || !inviteUrl}
                    onPress={() => void copyLink()}
                  >
                    <CopyIcon className="size-3.5" />
                    {copied ? t('gmod.copied') : t('gmod.copy')}
                  </Button>
                </div>
              )}

              {showSettings && (
                <div className="mt-3 grid gap-3 rounded-xl border border-separator bg-default/30 p-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] font-medium text-muted">{t('gmod.expiryLabel')}</span>
                    <SelectMenu
                      value={String(expiresIn)}
                      onChange={(v) => setExpiresIn(parseInt(v, 10))}
                      options={EXPIRY_OPTIONS.map((o) => ({ value: String(o.value), label: t(o.labelKey) }))}
                    />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] font-medium text-muted">{t('gmod.usesLabel')}</span>
                    <SelectMenu
                      value={String(maxUses)}
                      onChange={(v) => setMaxUses(parseInt(v, 10))}
                      options={USES_OPTIONS.map((o) => ({ value: String(o.value), label: t(o.labelKey) }))}
                    />
                  </label>
                  <div className="sm:col-span-2 flex justify-end">
                    <Button size="sm" variant="primary" isDisabled={genLoading} onPress={applySettings}>
                      {t('gmod.createNewLink')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
